import React from "react";
import { MoreVertical, Truck } from "lucide-react";

// Cấu hình hiển thị theo từng trạng thái thiết bị
const STATUS_CONFIG = {
  running: { label: "Đang chạy", dot: "bg-green-500", text: "text-green-600" },
  stopped: { label: "Dừng", dot: "bg-red-500", text: "text-red-600" },
  unlocked: { label: "Dừng mở khóa", dot: "bg-yellow-400", text: "text-yellow-600" },
  no_gps: { label: "Dừng GPS", dot: "bg-blue-500", text: "text-blue-600" },
  lost: { label: "Mất kết nối", dot: "bg-gray-400", text: "text-gray-500" },
};

/**
 * Logic: Xác định trạng thái của thiết bị dựa trên thời gian cập nhật và ACC
 */
const getDeviceStatus = (device) => {
  if (!device) return "lost";
  const now = Math.floor(Date.now() / 1000);
  const diff = now - (device.gpsdate_unix || 0);

  // Mất kết nối nếu dữ liệu cũ hơn 5 phút
  if (diff > 300) return "lost";

  // Kiểm tra đang chạy hay dừng dựa trên khóa điện (ACC) và tốc độ
  if (device.acc_status === "On" && device.speed_kmh > 0) return "running";
  if (device.acc_status === "On" && device.speed_kmh === 0) return "stopped";

  return "no_gps";
};

const formatTime = (unix) => {
  if (!unix) return "--";
  const d = new Date(unix * 1000);
  return d.toLocaleDateString("vi-VN") + " " + d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

/**
 * Thẻ hiển thị tóm tắt thiết bị (Sidebar Trái)
 */
const DeviceCard = ({ device, isSelected, onClick }) => {
  const status = getDeviceStatus(device);
  const cfg = STATUS_CONFIG[status];

  return (
    <div
      onClick={onClick}
      className={`w-full text-left cursor-pointer border-b border-gray-100 px-3 py-3 transition-colors hover:bg-[#f1f1fb] ${isSelected ? "bg-[#f1f1fb] border-l-2 border-l-[#7D7ACC]" : ""
        }`}
    >
      {/* Row 1: Icon tải + Tên thiết bị + Thời gian + Menu */}
      <div className="flex items-start gap-2 mb-2">
        <div className={`mt-0.5 w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[#7D7ACC]" : "bg-gray-400"}`}>
          <Truck size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs font-bold text-gray-800 truncate">{device?.imei || "N/A"}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className={`text-[10px] font-semibold ${cfg.text}`}>
                {/* Logic: Tính toán khoảng thời gian từ lúc cập nhật cuối đến hiện tại */}
                {device?.gpsdate_unix ? (() => {
                  const diff = Math.floor(Date.now() / 1000) - device.gpsdate_unix;
                  const days = Math.floor(diff / 86400);
                  const hrs = Math.floor((diff % 86400) / 3600);
                  const mins = Math.floor((diff % 3600) / 60);
                  if (days > 0) return `${days} ngày`;
                  if (hrs > 0) return `${hrs} giờ`;
                  return `${mins} phút`;
                })() : "--"}
              </span>
              <button className="text-gray-400 hover:text-gray-600 p-0.5"><MoreVertical size={14} /></button>
            </div>
          </div>

          {/* Dòng hiển thị chấm trạng thái và nhãn trạng thái tương ứng */}
          <div className="flex items-center gap-1 mt-0.5">
            <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`}></span>
            <span className={`text-[10px] font-semibold ${cfg.text}`}>{cfg.label}</span>
          </div>
        </div>
      </div>

      {/* Thông tin chi tiết thu gọn */}
      <div className="ml-10 space-y-0.5 text-[11px] text-gray-500">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 text-gray-400">📶</span>
          <span className="font-mono">{device?.imei || "--"}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 text-gray-400">🕐</span>
          <span>{formatTime(device?.gpsdate_unix)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 text-gray-400">📍</span>
          <span className="truncate">
            {device?.latitude ? `${device.latitude.toFixed(6)}, ${device.longitude.toFixed(6)}` : "--"}
          </span>
        </div>
      </div>

      {/* Các nút hành động nhanh bên dưới card */}
      <div className="flex items-center gap-0 ml-10 mt-2 border-t border-gray-100 pt-2">
        <button className="flex items-center gap-1 text-[10px] font-bold text-[#7D7ACC] hover:bg-orange-50 px-2 py-1 rounded transition-colors">📡 Giám sát</button>
        <button className="flex items-center gap-1 text-[10px] font-bold text-[#7D7ACC] hover:bg-orange-50 px-2 py-1 rounded transition-colors">🔄 Xem lại</button>
        <button className="flex items-center gap-1 text-[10px] font-bold text-[#7D7ACC] hover:bg-orange-50 px-2 py-1 rounded transition-colors">📋 Thông tin</button>
      </div>
    </div>
  );
};

export { getDeviceStatus };
export default DeviceCard;
